// A route drawn as what it is (CL13): a graph.
//
// The Routes view rendered `hops.map(h => h.tool).join(' → ')`, which is a faithful picture of a
// route that cannot branch and a LIE about one that can — the call route on cubi reads as
// "stt → call → tts → vad" when the VAD hangs off the microphone and the caller listens to hop 2.
// Four hops in a row is the one shape this route is not.
//
// Plain SVG, no graph library and no DOM measurement: the layout is computed by `buildRouteGraph`
// (pure, unit-tested) and this file only turns lanes and depths into coordinates. Node state —
// SR21's per-hop `degraded` with its idle seconds, and CL13's branch that had no provider — is
// drawn ON the node, because "which link went quiet" is the entire question an operator opens this
// page with.

import type { RouteGraphModel, RouteGraphNode } from '../lib/routeGraph';
import { SOURCE_ID } from '../lib/routeGraph';

/** What a live instance adds to a declared hop: is it moving, and if not, why not. */
export interface HopLiveState {
  /** `live` | `degraded` (SR21) | `skipped` (CL13: a branch with no live provider). */
  status?: string;
  /** The short sentence under the tool name — "no output 12s", "no provider". */
  note?: string;
  /** Throughput or any other one-line measurement to show under the label. */
  detail?: string;
}

const NODE_W = 208;
const NODE_H = 62;
const GAP_X = 64;
const GAP_Y = 22;
const PAD = 12;

const nodeX = (n: RouteGraphNode) => PAD + n.depth * (NODE_W + GAP_X);
const nodeY = (n: RouteGraphNode) => PAD + n.lane * (NODE_H + GAP_Y);

/** Keep a `service__tool` selector readable inside a fixed-width box without a tooltip-only label. */
export function truncateLabel(label: string, max = 26): string {
  if (label.length <= max) return label;
  return `${label.slice(0, max - 1)}…`;
}

/** The tone a node is drawn in — the single place the visual vocabulary is decided.
 *
 * `skipped` outranks `degraded`: a branch that was never instantiated is not a link that went
 * quiet, and showing it as a fault is how "the VAD is broken" gets reported for a VAD that is not
 * there at all. */
export function nodeTone(node: RouteGraphNode, live?: HopLiveState): string {
  if (node.broken) return 'bad';
  if (live?.status === 'skipped') return 'off';
  if (live?.status === 'degraded') return 'warn';
  if (node.kind === 'source') return 'source';
  if (node.isTap) return 'tap';
  if (node.isOutput) return 'out';
  return 'plain';
}

export function RouteGraph({
  graph,
  live,
  selectedHop,
  onSelectHop,
  title,
}: {
  graph: RouteGraphModel;
  /** Per-hop-INDEX live state (keys are hop indices, not positions — a skipped branch shortens
   *  nothing here, which is the same reason the gateway looks the DOWN end up by index). */
  live?: Record<number, HopLiveState | undefined>;
  selectedHop?: number | null;
  onSelectHop?: (index: number) => void;
  title?: string;
}) {
  const width = PAD * 2 + (graph.depth + 1) * NODE_W + graph.depth * GAP_X;
  const height = PAD * 2 + graph.lanes * NODE_H + (graph.lanes - 1) * GAP_Y;
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));

  return (
    <div className="route-graph-wrap">
      <svg
        className="route-graph"
        role="img"
        aria-label={title ?? 'route graph'}
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        data-testid="route-graph"
      >
        {graph.edges.map((e) => {
          const a = byId.get(e.from)!;
          const b = byId.get(e.to)!;
          const x1 = nodeX(a) + NODE_W;
          const y1 = nodeY(a) + NODE_H / 2;
          const x2 = nodeX(b);
          const y2 = nodeY(b) + NODE_H / 2;
          const mid = (x1 + x2) / 2;
          return (
            <path
              key={`${e.from}->${e.to}`}
              className={`route-edge${e.toTap ? ' route-edge-tap' : ''}`}
              d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
              data-tap={e.toTap ? 'true' : undefined}
            />
          );
        })}
        {graph.nodes.map((n) => {
          const state = n.kind === 'hop' ? live?.[n.index] : undefined;
          const tone = nodeTone(n, state);
          const x = nodeX(n);
          const y = nodeY(n);
          const badge = n.kind === 'source' ? 'UP' : n.isOutput ? 'OUT' : n.isTap ? 'TAP' : `#${n.index}`;
          return (
            <g
              key={n.id}
              className={`route-node route-node-${tone}${selectedHop === n.index ? ' is-selected' : ''}`}
              transform={`translate(${x} ${y})`}
              onClick={n.kind === 'hop' && onSelectHop ? () => onSelectHop(n.index) : undefined}
              data-testid={`route-node-${n.id}`}
              data-tone={tone}
            >
              <rect className="route-node-box" width={NODE_W} height={NODE_H} rx={8} />
              <text className="route-node-badge" x={NODE_W - 10} y={16} textAnchor="end">
                {badge}
              </text>
              <text className="route-node-label" x={10} y={24}>
                {truncateLabel(n.id === SOURCE_ID ? 'source (microphone)' : n.label)}
              </text>
              {state?.detail && (
                <text className="route-node-sub" x={10} y={40}>
                  {truncateLabel(state.detail, 30)}
                </text>
              )}
              {state?.note && (
                <text className="route-node-note" x={10} y={54}>
                  {truncateLabel(state.note, 30)}
                </text>
              )}
              {!state?.note && n.isTap && n.kind === 'hop' && (
                <text className="route-node-sub" x={10} y={state?.detail ? 54 : 40}>
                  side branch — read as a tap
                </text>
              )}
              {n.broken && (
                <text className="route-node-note" x={10} y={54}>
                  reads nothing valid
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
